import { css } from "@emotion/react";
import { useGateValue } from "@statsig/react-bindings";

import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { FundraiserResponse } from "@every.org/common/src/routes/fundraiser";

import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
  fullWidthButtonCss,
} from "src/components/Button";
import { Card } from "src/components/Card";
import { Icon, IconDisplay, IconSize } from "src/components/Icon";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { textSizeCss } from "src/theme/text";

interface FundraiserManagementCardProps {
  fundraiserData: FundraiserResponse;
  showUpdatesForViewer: boolean;
  showViewDonations: boolean;
}

const actionButtonCss = css`
  ${fullWidthButtonCss};
`;

const actionButtonContentCss = [
  horizontalStackCss.xs,
  css`
    align-items: center;
    justify-content: center;
  `,
];

const buttonsContainerCss = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      flex-direction: row;
      gap: ${spacing.m};
    `,
  })};

  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: css`
      flex-direction: column;
      gap: ${spacing.xs};
    `,
  })}
`;

export const FundraiserManagementCard = ({
  fundraiserData,
  showUpdatesForViewer,
  showViewDonations,
}: FundraiserManagementCardProps) => {
  const showFundraiserUpdatePosts = useGateValue(
    "show_fundraiser_update_posts"
  );
  const { fundraiser, nonprofits } = fundraiserData;
  const nonprofit = nonprofits[0];
  const showUpdates = showFundraiserUpdatePosts && showUpdatesForViewer;
  const viewDonationsPath = getRoutePath({
    name: ClientRouteName.FUNDRAISER_DONATIONS,
    format: URLFormat.RELATIVE,
    tokens: {
      nonprofitSlug: nonprofit.primarySlug,
      fundraiserSlug: fundraiser.slug,
    },
  });

  return (
    <Card css={verticalStackCss.m} data-tname="fundraiserManagementCard">
      <div css={buttonsContainerCss}>
        <Button
          role={ButtonRole.SECONDARY}
          size={ButtonSize.MEDIUM}
          buttonCss={actionButtonCss}
          onClick={{
            kind: ButtonTargetKind.LINK,
            to: getRoutePath({
              name: ClientRouteName.FUNDRAISER_EDIT,
              format: URLFormat.RELATIVE,
              tokens: {
                nonprofitSlug: nonprofit.primarySlug,
                fundraiserSlug: fundraiser.slug,
              },
            }),
          }}
          data-tname="manageFundraiserButton"
          contentCss={actionButtonContentCss}
        >
          <Icon
            iconImport={() => import("src/components/Icon/icons/SettingsIcon")}
            size={IconSize.MEDIUM}
            display={IconDisplay.CURRENT_COLOR}
          />
          <span>Manage Fundraiser</span>
        </Button>
        {showViewDonations && (
          <Button
            role={ButtonRole.SECONDARY}
            size={ButtonSize.MEDIUM}
            buttonCss={actionButtonCss}
            onClick={{
              kind: ButtonTargetKind.LINK,
              to: viewDonationsPath,
            }}
            data-tname="ViewFundraiserDonations"
            contentCss={actionButtonContentCss}
          >
            <Icon
              iconImport={() => import("@components/Icon/icons/DollarIcon")}
              size={IconSize.MEDIUM}
              display={IconDisplay.CURRENT_COLOR}
            />
            <span>View donations</span>
          </Button>
        )}
      </div>
      {showUpdates && (
        <div
          css={[
            horizontalStackCss.m,
            css`
              align-items: center;
              border-top: 1px solid var(${colorCssVars.dividerSoft});
              padding-top: ${spacing.m};
            `,
          ]}
        >
          <div
            css={[
              verticalStackCss.xxs,
              css`
                flex: 1;
                min-width: 0;
              `,
            ]}
          >
            <h4 css={textSizeCss.s}>Engage your donors</h4>
            <p
              css={[
                textSizeCss.xs,
                { color: `var(${colorCssVars.text.secondary})` },
              ]}
            >
              Post an update to email all current and past donors.
            </p>
          </div>
          <Button
            role={ButtonRole.PRIMARY}
            size={ButtonSize.MEDIUM}
            onClick={{
              kind: ButtonTargetKind.LINK,
              to: getRoutePath({
                name: ClientRouteName.FUNDRAISER_UPDATES,
                format: URLFormat.RELATIVE,
                tokens: {
                  nonprofitSlug: nonprofit.primarySlug,
                  fundraiserSlug: fundraiser.slug,
                },
              }),
            }}
            data-tname="fundraiserUpdatesButton"
          >
            Updates
          </Button>
        </div>
      )}
    </Card>
  );
};
